import { useSearchParams } from 'react-router-dom';
import { useListSavedQueries } from '@Inventory/services/savedQueryQueries';
import { ListFilterOption } from '@common/ui/ListFilters/common';
import GenericFilterSingleSelect from '@common/organisms/CommonFilters/GenericFilterSingleSelect';
import { CustomAnalyticsEvents } from '@utils/analytics';
import { useCallback, useMemo, useState } from 'react';
import { MaterialIcon } from '@components';
import { ButtonBase } from '@mui/material';
import { QUERY_QUERY_PARAM, SAVED_QUERY_LABEL, SAVED_QUERY_QUERY_PARAM } from './types';
import { SavedQueryDeleteDialog } from './SavedQueryDeleteDialog';
import { useHasPermission } from '@hooks';
import { Permission } from '@common/utils/admin_permissions';

export default function SavedQueryFilter() {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { savedQueries, isFetching: isLoading } = useListSavedQueries();
  const hasWriteInventoryPermissions = useHasPermission([Permission.INVENTORY_WRITE]);

  const options: ListFilterOption[] = useMemo(
    () =>
      savedQueries.map((savedQuery) => ({
        value: savedQuery.id,
        label: savedQuery.name,
        query: savedQuery.query,
        endIcon: hasWriteInventoryPermissions && <SavedQueryDeleteButton onClick={() => setDeleteId(savedQuery.id)} />,
      })),
    [savedQueries],
  );

  const queryToDelete = useMemo(
    () => savedQueries.find((savedQuery) => savedQuery.id === deleteId),
    [savedQueries, deleteId],
  );

  const handleOnSelect = useCallback(
    (value: ListFilterOption) => {
      const selectedQuery = savedQueries.find((savedQuery) => savedQuery.id === value.value);

      if (selectedQuery) {
        searchParams.set(QUERY_QUERY_PARAM, selectedQuery?.query || '');
      }

      searchParams.set(SAVED_QUERY_QUERY_PARAM, value.value);
      setSearchParams(searchParams);
    },
    [savedQueries, searchParams, setSearchParams],
  );

  return (
    <>
      <GenericFilterSingleSelect
        options={options}
        filterLabel={SAVED_QUERY_LABEL}
        filterParam={SAVED_QUERY_QUERY_PARAM}
        isLoading={isLoading}
        handleSelect={handleOnSelect}
        data-trigger={CustomAnalyticsEvents.INVENTORY_ACCESS_SCOPE_FILTER_APPLIES}
      />
      {queryToDelete && (
        <SavedQueryDeleteDialog
          savedQuery={queryToDelete}
          open={Boolean(queryToDelete)}
          onClose={() => setDeleteId(null)}
        />
      )}
    </>
  );
}

function SavedQueryDeleteButton({ onClick }: { onClick: () => void }) {
  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <ButtonBase onClick={handleDelete}>
      <MaterialIcon symbol="delete" />
    </ButtonBase>
  );
}
